import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { TrendingDown, DollarSign, XCircle } from 'lucide-react';
interface SlideProblemProps {
  onContinue: () => void;
}
export function SlideProblem({
  onContinue
}: SlideProblemProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600);
    const t2 = setTimeout(() => setStage(2), 1600);
    const t3 = setTimeout(() => setStage(3), 2600);
    const t4 = setTimeout(() => setStage(4), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-666">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-4" data-id="element-667">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-668">
          The Agency Bottleneck
        </h2>
        <p className="text-xl text-white/60" data-id="element-669">
          Why legacy brands die earlier than they need to.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-4xl mx-auto w-full space-y-6" data-id="element-670">
        <div className="space-y-6" data-id="element-671">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-672">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-673">
              <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-674" />
              The Cliff
            </h3>
            <p className="text-white/70" data-id="element-675">
              When patents expire, generic competition can cause sales to drop
              &gt;90% almost overnight. Marketing investment is stopped
              immediately.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          x: stage >= 2 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-676">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-677">
              <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-678" />
              The Cost Trap
            </h3>
            <p className="text-white/70" data-id="element-679">
              Traditional agency campaigns are too expensive to justify on
              declining revenue. The gap between revenue and cost approaches
              zero.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : -20
        }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-6" data-id="element-680">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-681">
              <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" data-id="element-682" />
              Premature Death
            </h3>
            <p className="text-white/90" data-id="element-683">
              The real problem isn't that these brands lack value — it's that
              traditional marketing economics force companies to abandon them.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 10
      }} className="bg-navy-800/60 border border-navy-700 rounded-xl px-6 py-4 mt-2" data-id="element-684">
          <p className="text-white/50 leading-relaxed text-sm" data-id="element-685">
            <span className="text-teal-400/80 font-medium" data-id="element-686">
              Note on biologics:
            </span>{' '}
            Biosimilar competition erodes revenue more gradually (15-30% per
            year), so marketing continues longer. But eventually the same
            dynamic applies — declining revenue meets fixed marketing costs, and
            attention fades. Both molecule and biologic brands face the same end
            state.
          </p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-687">
        <Button onClick={onContinue} data-id="element-688">Calculator</Button>
      </motion.div>
    </div>;
}